import { collection, getDocs, query, where } from "firebase/firestore";
import { Fragment, useEffect, useState } from "react";
import { db, useGames } from "./firebase";
import { EventDoc } from "./eventHandler";
import { EventType } from "qwick/dist/event";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";

const getUserIds = (events: EventDoc[]) => [...new Set(events.map(e => e.userId))];

type EventsByUser = Map<string, EventDoc[]>;

type EventsByLevel = EventsByUser[];

type EventsByGame = Map<string, EventsByLevel>;

const statTypes = [
    "playerCount",
    "playCount",
    "winCount",
    "playCountPerPlayer",
    "winCountPerPlayer",
    "winRate",
    "totalPlayTime",
    "playTimePerPlayer",
    "playTimePerPlay"
] as const;

type StatType = (typeof statTypes)[number];

const statText: Record<StatType, string> = {
    playerCount: "Player count",
    playCount: "Play count",
    winCount: "Win count",
    playCountPerPlayer: "Play count per player",
    winCountPerPlayer: "Win count per player",
    winRate: "Win rate",
    totalPlayTime: "Total play time",
    playTimePerPlayer: "Play time per player",
    playTimePerPlay: "Play time per play"
};

type Stat = Record<StatType, number>;

type StatsByLevel = Stat[];

type StatsByGame = Map<
    string,
    {
        statsByLevel: StatsByLevel;
        total: Stat;
    }
>;

const getEventsByUser = (events: EventDoc[]): EventsByUser => {
    const userIds = getUserIds(events);
    const byUser = new Map<string, EventDoc[]>(userIds.map(id => [id, []]));
    events.forEach(event => {
        byUser.get(event.userId)?.push(event);
    });
    return byUser;
};

const getEventsByLevel = (events: EventDoc[]): EventsByLevel => {
    const byLevel: EventDoc[][] = [];
    events.forEach(event => {
        if (event.event.levelNum === undefined) return;
        while (byLevel.length <= event.event.levelNum) byLevel.push([]);
        byLevel[event.event.levelNum].push(event);
    });
    return byLevel.map(e => getEventsByUser(e));
};

const getEventsByGame = (gameIds: string[], events: EventDoc[]): EventsByGame => {
    const byGame = new Map<string, EventDoc[]>(gameIds.map(id => [id, []]));
    events.forEach(event => {
        byGame.get(event.gameId)?.push(event);
    });
    return new Map(gameIds.map(id => [id, getEventsByLevel(byGame.get(id) ?? [])]));
};

const getUserPlayTime = (events: EventDoc[]): number => {
    const maxTimeInMillis = 1000 * 60 * 10;
    let result = 0;
    const eventsSorted = [...events].sort((a, b) => b.timestamp - a.timestamp);
    eventsSorted.forEach((e, i) => {
        if (e.event.type === EventType.LEVEL_START) return;
        for (let j = i + 1; j < eventsSorted.length; ++j) {
            if (eventsSorted[j].event.type !== EventType.LEVEL_START) continue;
            const time = e.timestamp - eventsSorted[j].timestamp;
            result += Math.min(time, maxTimeInMillis);
            break;
        }
    });
    return result;
};

const twoDigits = (n: number): string => n.toString().padStart(2, "0");

const formatTime = (timestamp: number): string => {
    if (isNaN(timestamp)) return "";
    let seconds = Math.floor(timestamp / 1000);
    let minutes = Math.floor(seconds / 60);
    seconds -= minutes * 60;
    const hours = Math.floor(minutes / 60);
    minutes -= hours * 60;
    return `${twoDigits(hours)}:${twoDigits(minutes)}:${twoDigits(seconds)}`;
};

const round1 = (n: number) => (isNaN(n) ? "" : n.toFixed(1));

const toPercent = (n: number) => (isNaN(n) ? "" : `${(100 * n).toFixed(0)} %`);

const getTotalPlayerCount = (eventsByLevel: EventsByLevel): number => {
    const userIds = new Set<string>();
    eventsByLevel.forEach(eventsByUser => {
        eventsByUser.forEach(events => {
            events.forEach(event => {
                userIds.add(event.userId);
            });
        });
    });
    return userIds.size;
};

const getStatsByLevel = (eventsByLevel: EventsByLevel): StatsByLevel => {
    const result: StatsByLevel = [];
    eventsByLevel.forEach((eventsByUser, level) => {
        const playerCount = eventsByUser.size;
        let playCount = 0;
        let winCount = 0;
        let totalPlayTime = 0;
        [...eventsByUser]
            .map(([_, e]) => e)
            .flat()
            .forEach((e: EventDoc) => {
                if (e.event.type === EventType.LEVEL_START) ++playCount;
                if (e.event.type === EventType.LEVEL_WON) ++winCount;
            });
        eventsByUser.forEach(e => {
            totalPlayTime += getUserPlayTime(e);
        });
        result.push({
            playerCount,
            playCount,
            winCount,
            playCountPerPlayer: playCount / playerCount,
            winCountPerPlayer: winCount / playerCount,
            winRate: winCount / playCount,
            totalPlayTime,
            playTimePerPlayer: totalPlayTime / playerCount,
            playTimePerPlay: totalPlayTime / playCount
        });
    });
    return result;
};

const sum = (list: number[]) => list.reduce((s, v) => s + v, 0);

const getStatsByGame = (eventsByGame: EventsByGame): StatsByGame => {
    const result: StatsByGame = new Map();
    eventsByGame.forEach((eventsByLevel, gameId) => {
        const statsByLevel = getStatsByLevel(eventsByLevel);
        const playerCount = getTotalPlayerCount(eventsByLevel);
        const playCount = sum(statsByLevel.map(stat => stat.playCount));
        const winCount = sum(statsByLevel.map(stat => stat.winCount));
        const totalPlayTime = sum(statsByLevel.map(stat => stat.totalPlayTime));
        const total: Stat = {
            playerCount,
            playCount,
            winCount,
            playCountPerPlayer: playCount / playerCount,
            winCountPerPlayer: winCount / playerCount,
            winRate: winCount / playCount,
            totalPlayTime,
            playTimePerPlayer: totalPlayTime / playerCount,
            playTimePerPlay: totalPlayTime / playCount
        };
        result.set(gameId, { statsByLevel, total });
    });
    return result;
};

const statToList = (stat: Stat): string[] => [
    String(stat.playerCount),
    String(stat.playCount),
    String(stat.winCount),
    round1(stat.playCountPerPlayer),
    round1(stat.winCountPerPlayer),
    toPercent(stat.winRate),
    formatTime(stat.totalPlayTime),
    formatTime(stat.playTimePerPlayer),
    formatTime(stat.playTimePerPlay)
];

const Stats = () => {
    const games = useGames();
    const [events, setEvents] = useState<EventDoc[]>();

    useEffect(() => {
        getDocs(query(collection(db, "events"), where("timestamp", ">=", Date.now() - 1000 * 60 * 60 * 24 * 7))).then(
            snapshot => {
                setEvents(snapshot.docs.map(doc => doc.data() as EventDoc));
            }
        );
    }, []);

    if (games === undefined) return <></>;
    if (events === undefined) return <></>;

    const eventsByGame = getEventsByGame(
        games.map(g => g.id),
        events
    );

    const keys = statTypes.map(t => statText[t]);

    const statsByGame = getStatsByGame(eventsByGame);

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell />
                        {keys.map(key => (
                            <TableCell align="right">
                                <b>{key}</b>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                {games.map(game => {
                    const statTotal = statsByGame.get(game.id)?.total;
                    if (statTotal === undefined) return <></>;
                    return (
                        <Fragment key={game.id}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <b>{game.title}</b>
                                    </TableCell>
                                    {statToList(statTotal).map(value => (
                                        <TableCell align="right">
                                            <b>{value}</b>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {statsByGame.get(game.id)?.statsByLevel.map((stat, level) => {
                                    return (
                                        <TableRow key={level}>
                                            <TableCell>Level {level + 1}</TableCell>
                                            {statToList(stat).map(value => (
                                                <TableCell align="right">{value}</TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Fragment>
                    );
                })}
            </Table>
        </TableContainer>
    );
};

export default Stats;
